'use client';

import Link from 'next/link';

import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WHATSAPP_URL = 'https://wa.me/966508686446';
const EASE_OUT = [0.23, 1, 0.32, 1] as const;
const VIEWPORT = { once: true, amount: 0.2 } as const;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: EASE_OUT },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

/**
 * Pricing section, a branded CTA that routes to the live catalog rather than
 * re-fetching it. The full, always-current package list (with real prices in
 * the tenant currency) already lives on /packages, so the landing links there
 * instead of duplicating the data through an extra API. Private sessions route
 * to WhatsApp / the founder's site.
 */
export function PricingSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section
      id="pricing"
      className="py-16 lg:py-24"
      style={{ background: 'var(--karama-accent)' }}
    >
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={stagger}
          className="mb-10"
        >
          <motion.p
            variants={fadeUp}
            className="font-karama-label mb-3 text-xs"
            style={{ color: 'var(--karama-purple)' }}
          >
            {isAr ? 'الأسعار' : 'Pricing'}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-karama-heading text-[var(--karama-secondary)]"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}
          >
            {isAr ? (
              <>
                تجربة متميزة.{' '}
                <span style={{ color: 'var(--karama-teal)' }}>
                  أسعار في متناول الجميع
                </span>
              </>
            ) : (
              <>
                Premium experience.{' '}
                <span style={{ color: 'var(--karama-teal)' }}>
                  Accessible pricing
                </span>
              </>
            )}
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-3 max-w-xl text-sm text-gray-700"
          >
            {isAr
              ? 'باقات الكلاسات وعروض البداية والعضويات. تصفّحوا الكتالوج الكامل واحجزوا في خطوتين.'
              : 'Class packages, intro offers, and memberships. Browse the full, up-to-date catalog and check out in a couple of taps.'}
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={stagger}
          className="grid gap-6 sm:grid-cols-3"
        >
          {/* Browse packages, the primary route into the live catalog. */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col border-2 bg-white p-6 sm:col-span-1"
            style={{ borderColor: 'var(--karama-purple)' }}
          >
            <h3
              className="font-karama-heading mb-2 text-xl"
              style={{ color: 'var(--karama-secondary)' }}
            >
              {isAr ? 'باقات الكلاسات والعضويات' : 'Class Packages & Memberships'}
            </h3>
            <p className="mb-5 text-sm text-gray-700">
              {isAr
                ? 'باقات ٥ و١٠ و٢٠ كلاس، وعروض البداية والعروض الموسمية.'
                : '5, 10, and 20-class packs, intro offers, and seasonal deals.'}
            </p>
            <Link
              href="/packages"
              className="k-btn-teal mt-auto w-full justify-center"
              style={{ background: 'var(--karama-purple)' }}
            >
              {isAr ? 'اطّلعوا على الباقات' : 'View Packages'}{' '}
              <ArrowRight size={14} className="rtl:-scale-x-100" />
            </Link>
          </motion.div>

          {/* Private sessions with Karama instructors. */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col border border-gray-100 bg-white p-6"
          >
            <p
              className="font-karama-label mb-2 text-xs"
              style={{ color: 'var(--karama-teal)' }}
            >
              {isAr ? 'كلاسات خاصة' : 'Private Sessions'}
            </p>
            <h3
              className="font-karama-heading mb-2 text-xl"
              style={{ color: 'var(--karama-secondary)' }}
            >
              {isAr ? 'مع مدرّبي كراما' : 'With Karama Instructors'}
            </h3>
            <p className="mb-5 text-sm text-gray-700">
              {isAr
                ? 'كلاسات فردية مصمَّمة وفق أهدافكم وجدولكم ومستواكم.'
                : 'One-on-one sessions tailored to your goals, schedule, and level.'}
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="k-btn-teal mt-auto w-full justify-center"
            >
              <MessageCircle size={14} />{' '}
              {isAr ? 'تواصلوا معنا على واتساب' : 'Contact Us on WhatsApp'}
            </a>
          </motion.div>

          {/* Train with the founder. */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col border border-gray-100 bg-white p-6"
          >
            <p
              className="font-karama-label mb-2 text-xs"
              style={{ color: 'var(--karama-purple)' }}
            >
              {isAr ? 'كلاسات خاصة مع لنا' : 'Private Sessions with Lana'}
            </p>
            <h3
              className="font-karama-heading mb-2 text-xl"
              style={{ color: 'var(--karama-secondary)' }}
            >
              {isAr ? 'تدرّبوا مع المؤسِّسة' : 'Train with the Founder'}
            </h3>
            <p className="mb-5 text-sm text-gray-700">
              {isAr
                ? 'تدريب شخصي مع لنا ناظر، متاح عبر موقعها الإلكتروني.'
                : 'Personal training with Lana Nazer, available through her site.'}
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="k-btn-purple mt-auto w-full justify-center"
            >
              <MessageCircle size={14} />{' '}
              {isAr ? 'تواصلوا معنا على واتساب' : 'Contact Us on WhatsApp'}
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
